// The sky pass alone, for the console profiler (perfProfile.ts, prof.sky()).
//
// The sky pass (sky.ts: one full-screen triangle that looks the colour up in the sky-view table) is drawn alone, on the GPU, with the live material, into an off-screen target the size
// of the drawing buffer. The live pass, the canvas and the app's state are not touched, so nothing flickers while it runs. A variant's first draws compile the shader and are not
// timed. Each figure is the median of several timer queries, with the 90th percentile beside it. Without the GPU timer extension the figures are a CPU wait on a one-pixel read, which
// also includes submit and driver time.
//
// The pass costs per pixel, so the figures scale with the resolution. "%" is the share of the "sky pass" row for the same view. (The cloud is the cloud volume's, timed with prof.frame.)
import * as THREE from 'three';
import type { Sky } from './sky';
import { GpuTimer, median, pct, fmt, sleep } from './profCommon';

export interface SkyProfileContext {
  renderer: THREE.WebGLRenderer;
  camera: THREE.PerspectiveCamera;
  sky: Sky;
}

export interface SkyRunOptions {
  /** Timer queries per variant and view and pass. Default 16. */
  samples?: number;
  /** Passes over the variants per view; each variant keeps its best (lowest) median, which discards a pass slowed by clock changes or contention. Default 2. */
  passes?: number;
  /** Draws of the pass inside one query, to lift a small cost above the timer's resolution. Default 2. */
  repeat?: number;
  /** Size to draw at, in pixels; default the drawing buffer's. */
  size?: { w: number; h: number };
  /** Views, all in the camera's heading except 'current': 'current' (the camera as it is), 'horizon' (5 degrees up), 'mid' (35), 'zenith' (85). Default all four. */
  views?: Array<'current' | 'horizon' | 'mid' | 'zenith'>;
  /** Variant names to run (see prof.skyVariants); default all. */
  only?: string[];
  /** Print the tables. Default true. */
  print?: boolean;
}

interface Variant {
  name: string;
  note: string;
  flat?: boolean;            // a plain colour instead of the shader: the cost of filling the screen
}

const VARIANTS: Variant[] = [
  { name: 'fill floor', note: 'a plain colour over the screen: what filling it costs', flat: true },
  { name: 'sky pass',   note: 'the sky pass as it is drawn: the sky-view table looked up, scaled, toned and dithered' },
];

const VIEW_PITCH: Record<string, number | null> = { current: null, horizon: 5, mid: 35, zenith: 85 };

export const SKY_VARIANT_NOTES = VARIANTS.map(v => ({ name: v.name.trim(), note: v.note }));

/** Returns the run() function. `acquire` takes the profiler's lock (throws if a measurement is running) and returns the release. */
export function createSkyRunner(ctx: SkyProfileContext, timer: GpuTimer, acquire: (what: string) => () => void, envInfo: () => Record<string, any>) {
  const { renderer, camera, sky } = ctx;
  return async function run(o: SkyRunOptions = {}) {
    if (!sky.mesh.visible) throw new Error('prof.sky: the sky is not drawn yet (no atmosphere or sun state has arrived)');
    const release = acquire('sky');
    const samples = o.samples ?? 16, repeat = o.repeat ?? 2, passes = Math.max(1, o.passes ?? 2);
    const bufSize = renderer.getDrawingBufferSize(new THREE.Vector2());
    const w = o.size?.w ?? Math.max(2, Math.round(bufSize.x)), h = o.size?.h ?? Math.max(2, Math.round(bufSize.y));
    const rt = new THREE.WebGLRenderTarget(w, h, { type: THREE.UnsignedByteType, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    const pm = sky.mesh.material as THREE.ShaderMaterial;       // the live material: its uniforms are the live ones
    const flatMat = new THREE.MeshBasicMaterial({ color: 0x6a85a8, depthTest: false, depthWrite: false, fog: false, toneMapped: false });
    const pScene = new THREE.Scene();
    const pMesh = new THREE.Mesh<THREE.BufferGeometry, THREE.Material>(sky.mesh.geometry, pm);
    pMesh.frustumCulled = false;
    pMesh.onBeforeRender = sky.mesh.onBeforeRender;
    pScene.add(pMesh);
    const cam = camera.clone() as THREE.PerspectiveCamera;
    cam.aspect = w / h; cam.updateProjectionMatrix();
    const px = new Uint8Array(4);
    const savedTarget = renderer.getRenderTarget();

    // The render target is set only inside one synchronous batch and put back before any await, so the app's own frames
    // (which run between our awaits) draw to the canvas as usual.
    const batch = (fn: () => void) => {
      renderer.setRenderTarget(rt);
      try { fn(); } finally { renderer.setRenderTarget(savedTarget); }
    };
    const draw = () => renderer.render(pScene, cam);
    const sync = () => renderer.readRenderTargetPixels(rt, 0, 0, 1, 1, px);

    const measure = async (): Promise<number[]> => {
      if (timer.available) {
        const qs: WebGLQuery[] = [];
        batch(() => { for (let i = 0; i < samples; i++) qs.push(timer.time(() => { for (let r = 0; r < repeat; r++) draw(); })); });
        return (await timer.resolve(qs)).map(v => v / repeat);
      }
      const out: number[] = [];
      for (let i = 0; i < samples; i++) {
        batch(() => { const t0 = performance.now(); for (let r = 0; r < repeat; r++) draw(); sync(); out.push((performance.now() - t0) / repeat); });
        if (i % 4 === 3) await sleep(0);
      }
      return out;
    };

    const views = o.views ?? ['current', 'horizon', 'mid', 'zenith'];
    const wanted = o.only ? VARIANTS.filter(v => o.only!.includes(v.name.trim())) : VARIANTS;
    const heading = new THREE.Vector3(); camera.getWorldDirection(heading); heading.y = 0;
    if (heading.lengthSq() < 1e-6) heading.set(0, 0, -1);
    heading.normalize();
    const pos = camera.getWorldPosition(new THREE.Vector3());
    const rows: Array<{ variant: string; view: string; ms: number; p90: number; samples: number; spread: number }> = [];

    timer.reset();
    try {
      for (const view of views) {
        const pitch = VIEW_PITCH[view];
        if (pitch === null) { cam.position.copy(camera.position); cam.quaternion.copy(camera.quaternion); }
        else {
          const p = THREE.MathUtils.degToRad(pitch);
          cam.position.copy(pos); cam.up.set(0, 1, 0);
          cam.lookAt(pos.x + heading.x * Math.cos(p), pos.y + Math.sin(p), pos.z + heading.z * Math.cos(p));
        }
        cam.updateMatrixWorld(true);
        // Ramp the GPU's clock up first: an integrated GPU idles low and the first rows would read slow.
        pMesh.material = pm;
        try { await renderer.compileAsync(pScene, cam); } catch { /* compiled by the first draw */ }
        { const t0 = performance.now(); while (performance.now() - t0 < 400) { batch(() => { for (let i = 0; i < 4; i++) draw(); sync(); }); await sleep(0); } }
        const got = new Map<string, Array<{ ms: number; p90: number; n: number }>>();
        for (let pass = 0; pass < passes; pass++) {
          for (const v of wanted) {
            pMesh.material = v.flat ? flatMat : pm;
            try { await renderer.compileAsync(pScene, cam); } catch { /* the first draw compiles instead */ }
            batch(() => { for (let i = 0; i < 3; i++) draw(); sync(); });     // warm-up, not timed
            const ms = (await measure()).filter(Number.isFinite);
            const list = got.get(v.name) ?? []; list.push({ ms: median(ms), p90: pct(ms, 0.9), n: ms.length }); got.set(v.name, list);
          }
        }
        for (const v of wanted) {
          const list = got.get(v.name)!, best = list.reduce((m, x) => (x.ms < m.ms ? x : m));
          const worst = list.reduce((m, x) => (x.ms > m.ms ? x : m));
          rows.push({ variant: v.name, view, ms: best.ms, p90: best.p90, samples: best.n, spread: best.ms > 0 ? (worst.ms - best.ms) / best.ms : 0 });
        }
      }
    } finally {
      renderer.setRenderTarget(savedTarget);
      rt.dispose(); flatMat.dispose();
      release();
    }

    const fullBy: Record<string, number> = {};
    for (const r of rows) if (r.variant === 'sky pass') fullBy[r.view] = r.ms;
    const table = wanted.map(v => {
      const row: Record<string, string> = { variant: v.name };
      for (const view of views) {
        const r = rows.find(x => x.variant === v.name && x.view === view)!;
        const f = fullBy[view];
        row[view] = `${fmt(r.ms)} ms (p90 ${fmt(r.p90)})` + (f ? `  ${fmt(100 * r.ms / f, 0)}%` : '') + (r.spread > 0.25 ? '  ?' : '');
      }
      return row;
    });
    const env: Record<string, any> = { ...envInfo(), drawnAt: `${w}x${h}` };
    const result = { kind: 'sky', env, samples, repeat, passes, rows, notes: Object.fromEntries(wanted.map(v => [v.name.trim(), v.note])) };
    if (o.print !== false) {
      console.log(`%cprof.sky  ${env.gpu}  ${w}x${h}  timing: ${env.timing}`, 'font-weight:bold');
      console.table(table);
      console.log('Each cell: best median GPU ms per draw of the pass over the passes (90th percentile), then the share of the "sky pass" row for that view. What each row does:');
      console.table(result.notes);
      if (timer.disjoints) console.warn(`prof.sky: the GPU reported ${timer.disjoints} disjoint event(s) (clock change); run again if the figures look odd.`);
    }
    return result;
  };
}
